import { useEffect } from 'react'
import { BrowserRouter, Navigate, Route, Routes } from 'react-router-dom'
import { QueryClient, QueryClientProvider } from '@tanstack/react-query'
import LoginPage from '@/pages/auth/LoginPage'
import MicrosoftCallbackPage from '@/pages/auth/MicrosoftCallbackPage'
import DevicePendingPage from '@/pages/auth/DevicePendingPage'
import ActivatePage from '@/pages/ActivatePage'
import ChangePasswordPage from '@/pages/ChangePasswordPage'
import AccessDeniedPage from '@/pages/AccessDeniedPage'
import AppLayout from '@/components/layout/AppLayout'
import ProtectedRoute from '@/components/auth/ProtectedRoute'
import ModuleRoute from '@/components/auth/ModuleRoute'
import DashboardPage from '@/pages/DashboardPage'
import PaymentsPage from '@/pages/PaymentsPage'
import ApprovalsPage from '@/pages/ApprovalsPage'
import VendorsPage from '@/pages/VendorsPage'
import UsersPage from '@/pages/UsersPage'
import AccountPage from '@/pages/AccountPage'
import SettingsPage from '@/pages/SettingsPage'
import { useAuthStore } from '@/store/authStore'
import type { User } from '@portal/types'

const queryClient = new QueryClient({
  defaultOptions: {
    queries: { staleTime: 30_000, retry: 1, refetchOnWindowFocus: false },
  },
})

function AppRoutes() {
  const initializeFromStorage = useAuthStore((s) => s.initializeFromStorage)
  const setAuthPartial = useAuthStore((s) => s.setAuthPartial)

  useEffect(() => {
    initializeFromStorage()
  }, [initializeFromStorage])

  // Listen for permission updates dispatched by the Axios interceptor after token refresh
  useEffect(() => {
    const handler = (e: Event) => {
      const { user, modules, accessToken } = (e as CustomEvent<{
        user: User
        modules: string[]
        accessToken: string
      }>).detail
      if (user && accessToken) {
        setAuthPartial(user, accessToken, modules ?? [])
      }
    }
    window.addEventListener('portal:auth-refreshed', handler)
    return () => window.removeEventListener('portal:auth-refreshed', handler)
  }, [setAuthPartial])

  return (
    <Routes>
      {/* Public routes */}
      <Route path="/login" element={<LoginPage />} />
      <Route path="/auth/microsoft/callback" element={<MicrosoftCallbackPage />} />
      <Route path="/device-pending" element={<DevicePendingPage />} />
      <Route path="/activate" element={<ActivatePage />} />

      {/* Password change — requires auth but no module guard */}
      <Route element={<ProtectedRoute />}>
        <Route path="/change-password" element={<ChangePasswordPage />} />

        {/* All other protected routes wrapped in AppLayout */}
        <Route element={<AppLayout />}>
          <Route path="/access-denied" element={<AccessDeniedPage />} />

          <Route path="/dashboard" element={<DashboardPage />} />

          <Route element={<ModuleRoute module="PAYMENTS" />}>
            <Route path="/payments" element={<PaymentsPage />} />
          </Route>

          <Route element={<ModuleRoute module="APPROVALS" />}>
            <Route path="/approvals" element={<ApprovalsPage />} />
          </Route>

          <Route element={<ModuleRoute module="VENDORS" />}>
            <Route path="/vendors" element={<VendorsPage />} />
          </Route>

          <Route element={<ModuleRoute module="USERS" />}>
            <Route path="/users" element={<UsersPage />} />
          </Route>

          <Route path="/account" element={<AccountPage />} />
          <Route path="/settings" element={<SettingsPage />} />
        </Route>
      </Route>

      {/* Redirects */}
      <Route path="/" element={<Navigate to="/dashboard" replace />} />
      <Route path="*" element={<Navigate to="/dashboard" replace />} />
    </Routes>
  )
}

export default function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <BrowserRouter>
        <AppRoutes />
      </BrowserRouter>
    </QueryClientProvider>
  )
}
